import {
  cutlistConfigs,
  type Ticker,
  tickers,
} from '@cutr/constants/cutlist-theme.ts';
import { create } from 'zustand';

import { useMaterialGroupState } from './api/materialsGroup';
import { useCutlistState } from './api/store';
import {
  BNBLogo,
  BouwProfiLogo,
  CutrLogo,
  FinsaLogo,
  JongeneelLogo,
  LeeuwerikLogo,
  SaliceLogo,
  SorpetalerLogo,
  StihoLogo,
  SwissKronoLogo,
  TimmerpaneelLogo,
  VerdouwLogo,
} from './blocks/Logos';
import { useCurrentFeaturesFromProvider } from './FeatureProvider';
import { SessionStorage } from './utils/storage';

type UiConfig = {
  logo: () => React.JSX.Element;
  backBtnKey: string;
  backBtnName?: string;
  backBtnUrl: string;
  baseUrl: string;
  logoUrl?: string;
  newAccountUrl?: string;
  seeCustomerNumberUrl?: string;
  loginUrl?: (language: string) => string;
  favicon: string;
  welcomeImage?: string;
  start: string;
  showCutrLabel?: boolean;
  pageTitleName: string;
  allowedRoutes?: string[];
  i18nNamespace?: string;
};

const cutlistUiConfigs: Record<Ticker, UiConfig> = {
  CUTR: {
    logo: CutrLogo,
    backBtnKey: 'common.cta.backHome',
    backBtnUrl: '/',
    baseUrl: '/',
    favicon: 'favicon.ico',
    start: '/',
    newAccountUrl: 'mailto:jasper.mittelmeijer@cutr.com',
    pageTitleName: 'Demo Environment',
  },
  'CUTR-EMBED': {
    logo: CutrLogo,
    backBtnKey: 'common.cta.backHome',
    backBtnUrl: '/',
    baseUrl: '/',
    favicon: 'favicon.ico',
    start: '/',
    pageTitleName: 'Cutr',
  },
  BNB: {
    logo: BNBLogo,
    backBtnKey: 'common.cta.backTo',
    backBtnName: 'Baars & Bloemhoff',
    backBtnUrl: '/home',
    baseUrl: 'https://www.baars-bloemhoff.nl/',
    logoUrl: 'https://www.baars-bloemhoff.nl/assortiment/',
    newAccountUrl: 'https://www.baars-bloemhoff.nl/account',
    seeCustomerNumberUrl: 'https://www.baars-bloemhoff.nl/',
    favicon: 'favicon-bnb.ico',
    start: '/home',
    welcomeImage: '/bnb-landing.png',
    showCutrLabel: false,
    pageTitleName: 'Baars & Bloemhoff',
  },
  STIHO: {
    logo: StihoLogo,
    backBtnKey: 'common.cta.backTo',
    backBtnName: 'Stiho',
    backBtnUrl: '/home',
    baseUrl: 'https://www.stiho.nl/',
    logoUrl: 'https://www.stiho.nl/',
    newAccountUrl: 'https://www.stiho.nl/klantworden',
    seeCustomerNumberUrl: 'https://www.stiho.nl/account',
    favicon: 'favicon-stiho.ico',
    start: '/home',
    welcomeImage: '',
    pageTitleName: 'Stiho',
  },
  SALICE: {
    logo: SaliceLogo,
    backBtnKey: 'common.cta.backTo',
    backBtnName: 'Salice',
    backBtnUrl: '/home',
    baseUrl: 'https://www.salice.ro/',
    logoUrl: 'https://www.salice.ro/',
    newAccountUrl: 'https://salice.ro/customer/account/create/',
    seeCustomerNumberUrl: 'https://www.salice.ro/',
    favicon: 'favicon-salice.ico',
    start: '/home',
    welcomeImage: '',
    pageTitleName: 'Salice',
  },
  TIMMERPANEEL: {
    logo: TimmerpaneelLogo,
    backBtnKey: 'common.cta.backTo',
    backBtnName: 'Timmerpaneel',
    backBtnUrl: '/home',
    baseUrl: 'https://www.timmerpaneel.nl/',
    logoUrl: 'https://www.timmerpaneel.nl/',
    newAccountUrl: 'https://www.timmerpaneel.nl/',
    seeCustomerNumberUrl: 'https://www.timmerpaneel.nl/',
    favicon: 'favicon.ico',
    start: '/home',
    welcomeImage: '',
    pageTitleName: 'Timmerpaneel',
  },
  JONGENEEL: {
    logo: JongeneelLogo,
    backBtnKey: 'common.cta.backTo',
    backBtnName: 'Jongeneel',
    backBtnUrl: '/home',
    baseUrl: 'https://www.jongeneel.nl/',
    logoUrl: 'https://www.jongeneel.nl/',
    newAccountUrl: 'https://www.jongeneel.nl/',
    seeCustomerNumberUrl: 'https://www.jongeneel.nl/',
    favicon: 'favicon-jongeneel.ico',
    start: '/home',
    welcomeImage: '',
    pageTitleName: 'Jongeneel',
  },
  LEEUWERIK: {
    logo: LeeuwerikLogo,
    backBtnKey: 'common.cta.backTo',
    backBtnName: 'Leeuwerik',
    backBtnUrl: '/home',
    baseUrl: 'https://www.leeuwerik.nl/',
    logoUrl: 'https://www.leeuwerik.nl/',
    newAccountUrl: 'https://www.leeuwerik.nl/',
    seeCustomerNumberUrl: 'https://www.leeuwerik.nl/',
    favicon: 'favicon-leeuwerik.ico',
    start: '/home',
    welcomeImage: '',
    pageTitleName: 'Leeuwerik',
  },
  // Same branding as LEEUWERIK; only the ticker differs so demo material
  // uploads stay isolated from the real Leeuwerik feed.
  'LEEUWERIK-DEMO': {
    logo: LeeuwerikLogo,
    backBtnKey: 'common.cta.backTo',
    backBtnName: 'Leeuwerik',
    backBtnUrl: '/home',
    baseUrl: 'https://www.leeuwerik.nl/',
    logoUrl: 'https://www.leeuwerik.nl/',
    newAccountUrl: 'https://www.leeuwerik.nl/',
    seeCustomerNumberUrl: 'https://www.leeuwerik.nl/',
    favicon: 'favicon-leeuwerik.ico',
    start: '/home',
    welcomeImage: '',
    pageTitleName: 'Leeuwerik',
    i18nNamespace: 'leeuwerik',
  },
  SORPETALER: {
    logo: SorpetalerLogo,
    backBtnKey: 'common.cta.backTo',
    backBtnName: 'Sorpetaler',
    backBtnUrl: '/home',
    baseUrl: 'https://www.sorpetaler.de/',
    logoUrl: 'https://www.sorpetaler.de/',
    newAccountUrl: 'https://www.sorpetaler.de/',
    seeCustomerNumberUrl: 'https://www.sorpetaler.de/',
    loginUrl: (language: string) => {
      const isProduction = import.meta.env.VITE_CUTR_ENV === 'production';
      const languagePath = language === 'de' ? '' : 'en/';
      const loginUrl = new URL(
        isProduction
          ? `https://www.sorpetaler.de/${languagePath}login-page/`
          : `https://www.sorpetaler.de/${languagePath}login-page/staging/`
      );
      loginUrl.searchParams.set('redirectUrl', window.location.toString());
      return loginUrl.toString();
    },
    favicon: 'favicon-sorpetaler.ico',
    start: '/home',
    welcomeImage: '',
    pageTitleName: 'Sorpetaler',
  },
  VERDOUW: {
    logo: VerdouwLogo,
    backBtnKey: 'common.cta.backTo',
    backBtnName: 'Verdouw',
    backBtnUrl: ['development', 'staging', 'preview'].includes(
      import.meta.env.VITE_CUTR_ENV
    )
      ? 'https://canary.verdouw.nu/cart'
      : 'https://www.verdouw.nu/cart',
    baseUrl: 'https://www.verdouw.nu/',
    logoUrl: '#',
    newAccountUrl: 'https://www.verdouw.nu/',
    seeCustomerNumberUrl: 'https://www.verdouw.nu/',
    favicon: 'favicon-verdouw.ico',
    start: '/home',
    welcomeImage: '/verdouw-landing.png',
    pageTitleName: 'Verdouw',
    allowedRoutes: [
      '/cutlist/:id/',
      '/cutlist/:id/parts',
      '/open',
      '/edit/:id',
      '/admin',
      '/admin/cutlists',
      '/admin/cutlists/orders',
      '/admin/cutlists/orders/:id',
      '/admin/cutlists/orders/:id/edit',
    ],
  },
  BOUWPROFI: {
    logo: BouwProfiLogo,
    backBtnKey: 'common.cta.backTo',
    backBtnName: 'Bouwprofi',
    backBtnUrl: ['development', 'staging', 'preview'].includes(
      import.meta.env.VITE_CUTR_ENV
    )
      ? 'https://canary.bouwprofi.nl/cart'
      : 'https://www.bouwprofi.nl/cart',
    baseUrl: 'https://www.bouwprofi.nl/',
    logoUrl: '#',
    newAccountUrl: 'https://www.bouwprofi.nl/',
    seeCustomerNumberUrl: 'https://www.bouwprofi.nl/',
    favicon: 'favicon-bouwprofi.ico',
    start: '/home',
    welcomeImage: '/bouwprofi-landing.png',
    pageTitleName: 'Bouwprofi',
    allowedRoutes: [
      '/cutlist/:id/',
      '/cutlist/:id/parts',
      '/open',
      '/edit/:id',
      '/admin',
      '/admin/cutlists',
      '/admin/cutlists/orders',
      '/admin/cutlists/orders/:id',
      '/admin/cutlists/orders/:id/edit',
    ],
  },
  SWISSKRONO: {
    logo: SwissKronoLogo,
    backBtnKey: 'common.cta.backTo',
    backBtnName: 'Swiss Krono',
    backBtnUrl: '/home',
    baseUrl: 'https://www.swisskrono.com/',
    favicon: 'favicon-swisskrono.ico',
    start: '/home',
    welcomeImage: '',
    pageTitleName: 'Swiss Krono',
  },
  FINSA: {
    logo: FinsaLogo,
    backBtnKey: 'common.cta.backTo',
    backBtnName: 'Finsa',
    backBtnUrl: '/home',
    baseUrl: 'https://www.finsa.com/',
    favicon: 'favicon-finsa.ico',
    start: '/home',
    welcomeImage: '',
    pageTitleName: 'Finsa',
  },
};

const demoHostnames: Record<string, Ticker> = {
  '600v09.demo.cutr.com': 'JONGENEEL',
  'brbxdh.demo.cutr.com': 'LEEUWERIK-DEMO',
};

interface TickerState {
  ticker: Ticker;
  setTicker: (ticker: Ticker) => void;
}
const useTickerStore = create<TickerState>((set) => ({
  ticker: initTicker(),
  setTicker: (ticker: Ticker) => set(() => ({ ticker })),
}));

export const useTicker = () => useTickerStore((state) => state.ticker);
export const getTicker = () => useTickerStore.getState().ticker;

function initTicker(): Ticker {
  const tickerOverride = getTickerOverride();
  if (tickerOverride) return tickerOverride;

  if (window.location.hostname.endsWith('.demo.cutr.com')) {
    const demoTicker = demoHostnames[window.location.hostname];
    if (demoTicker) return demoTicker;
    throw Error('This demo subdomain does not exist.');
  }

  if (import.meta.env.VITE_CUTR_ENV === 'staging') {
    const stagingTickerOverride = getStagingTickerOverride();
    if (stagingTickerOverride) return stagingTickerOverride;
  }

  for (const key of tickers) {
    const tickerConfig = cutlistConfigs[key] || {};

    if (window.location.hostname === tickerConfig.hostname) return key;
  }

  return tickers[2]; //BNB,
}

function getStagingTickerOverride(): Ticker | undefined {
  for (const key of tickers) {
    const tickerConfig = cutlistConfigs[key] || {};

    const currentSubdomain = window.location.hostname.split('.')[0];
    const partnerSubdomain = tickerConfig.hostname.split('.')[0];

    if (currentSubdomain === partnerSubdomain) return key;
  }

  throw Error('This staging subdomain does not exist.');
}

function getTickerOverride(): Ticker | undefined {
  if (import.meta.env.VITE_CUTR_ENV === 'production') return;
  if (import.meta.env.VITE_CUTR_ENV === 'staging') return;

  const urlParams = new URLSearchParams(window.location.search);
  const tickerFromParam = urlParams.get('t') as Ticker;

  const sessionTicker = SessionStorage.get('ticker');
  if (tickers.includes(tickerFromParam)) {
    // if theme has changed, cleanup
    if (sessionTicker && tickerFromParam !== sessionTicker) {
      useCutlistState.getState().reset();
      useMaterialGroupState.getState().clear();

      // reload to start from a fresh state
      window.location.reload();
    }

    SessionStorage.set('ticker', tickerFromParam);
    return tickerFromParam;
  }

  if (tickers.includes(sessionTicker as Ticker)) {
    return sessionTicker as Ticker;
  }
}

export const getSourceConfig = () => {
  const ticker = getTicker();

  return {
    ...cutlistConfigs[ticker],
    ...cutlistUiConfigs[ticker],
  };
};

export const useCurrentFeatures = () => {
  const features = useCurrentFeaturesFromProvider();
  return features;
};
export const getCurrentFeatures = () => {
  const config = getSourceConfig();
  return config.features;
};

export const isCutrEmbedded = () => {
  const ticker = getTicker();
  return ticker === 'CUTR-EMBED';
};

export const isStarterTier = () => getSourceConfig().pricingTier === 'starter';
